import { useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import EditIcon from '@mui/icons-material/Edit';
import { useAppForm } from '~/components/Form';
import { FormattedMessage, useIntl } from 'react-intl';
import messages from './messages';
import { useUpdatePublisherEmergencyStatus } from '~/hooks/api/useUpdatePublisherEmergencyStatus';
import styled from '@emotion/styled';
import { PublisherEmergencyStatus } from '~/utils/publisher-emergency-status';

type StatusEditButtonProps = {
  publisherEmergencyReportId: number;
  currentStatus: PublisherEmergencyStatus;
  statusLabel: string;
};

type StatusFormValue = 'Acknowledged' | 'Safe' | '';

type FormValues = {
  status: StatusFormValue;
  statusMessage: string;
};

const StyledLabel = styled.div`
  text-align: left;
  flex: 1;
`;

const toStatusFormValue = (
  status: PublisherEmergencyStatus
): StatusFormValue => {
  if (status === PublisherEmergencyStatus.Acknowledged) return 'Acknowledged';
  if (status === PublisherEmergencyStatus.Safe) return 'Safe';
  return '';
};

const fromStatusFormValue = (
  status: StatusFormValue
): PublisherEmergencyStatus | null => {
  if (status === 'Acknowledged') return PublisherEmergencyStatus.Acknowledged;
  if (status === 'Safe') return PublisherEmergencyStatus.Safe;
  return null;
};

const StatusEditButton = ({
  publisherEmergencyReportId,
  currentStatus,
  statusLabel,
}: StatusEditButtonProps) => {
  const intl = useIntl();
  const [isDialogVisible, setDialogVisibilty] = useState(false);

  const handleDialogClose = () => {
    setDialogVisibilty(false);
  };
  const updateStatusMutation = useUpdatePublisherEmergencyStatus({
    onSuccess() {
      handleDialogClose();
    },
  });

  const form = useAppForm({
    defaultValues: {
      status: toStatusFormValue(currentStatus),
      statusMessage: '',
    },
    onSubmit: async ({ value }: { value: FormValues }) => {
      const nextStatus = fromStatusFormValue(value.status);
      if (nextStatus === null) return;

      const mutationData = {
        publisherEmergencyReportId,
        newStatus: nextStatus,
        statusMessage: value.statusMessage,
      };
      updateStatusMutation.mutate(mutationData);
    },
  });

  const handleButtonClick = () => {
    form.reset();
    setDialogVisibilty(true);
  };

  const getButtonColor = () => {
    if (currentStatus === PublisherEmergencyStatus.Safe) return 'success';
    if (currentStatus === PublisherEmergencyStatus.Unknown) return 'error';
    if (currentStatus === PublisherEmergencyStatus.Acknowledged)
      return 'warning';
    return 'primary';
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        color={getButtonColor()}
        sx={{
          width: 190,
          minWidth: 190,
          justifyContent: 'space-between',
          textAlign: 'left',
        }}
        onClick={handleButtonClick}
      >
        <StyledLabel>{statusLabel}</StyledLabel>

        <EditIcon sx={{ fontSize: 16 }} />
      </Button>

      <Dialog
        open={isDialogVisible}
        onClose={handleDialogClose}
        aria-labelledby="status-edit-dialog-title"
        aria-describedby="status-edit-dialog-description"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <DialogTitle id="status-edit-dialog-title">
            <FormattedMessage {...messages.statusEditDialogTitle} />
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <DialogContentText>
                <FormattedMessage {...messages.statusEditDialogDescription} />
              </DialogContentText>
              <form.AppField name="status">
                {(field) => (
                  <field.RadioGroupField
                    label={<FormattedMessage {...messages.status} />}
                    aria-labelledby="status-selection"
                    options={[
                      {
                        value: 'Acknowledged',
                        label: <FormattedMessage {...messages.Acknowledged} />,
                      },
                      {
                        value: 'Safe',
                        label: (
                          <FormattedMessage
                            {...messages.reportAsSafeDialogConfirm}
                          />
                        ),
                      },
                    ]}
                  />
                )}
              </form.AppField>
              <form.AppField name="statusMessage">
                {(field) => (
                  <field.TextField
                    label={
                      <FormattedMessage
                        {...messages.statusEditStatusMessageLabel}
                      />
                    }
                    placeholder={intl.formatMessage(
                      messages.statusEditStatusMessagePlaceholder
                    )}
                  />
                )}
              </form.AppField>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose}>
              <FormattedMessage {...messages.statusEditCancel} />
            </Button>
            <form.AppForm>
              <form.SubmitButton>
                <FormattedMessage {...messages.statusEditSave} />
              </form.SubmitButton>
            </form.AppForm>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default StatusEditButton;
